import React, { useState } from 'react';
import {
  Container,
  Header,
  Form,
  FormField,
  Input,
  Button,
  SpaceBetween,
  Alert,
  Modal,
  Box
} from '@cloudscape-design/components';
import { login } from '../utils/auth';

const LoginForm = ({ onLoginSuccess, onLoginFailure }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');


    try {
      const result = await login(username, password);
      
      if (result.success) {
        onLoginSuccess?.(result);
      } else {
        // 确保错误信息是字符串
        const errorMessage = typeof result.error === 'string' ? result.error : 
                           result.message || '登录失败';
        setError(errorMessage);
        onLoginFailure?.(result);
      }
    } catch (error) {
      console.error('❌ 登录异常:', error);
      // 确保错误信息是字符串
      const errorMessage = error.message || '登录系统错误，请重试';
      setError(errorMessage);
      onLoginFailure?.({ success: false, error: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  // 注册处理函数
  const handleRegister = async () => {
    setRegisterLoading(true);
    setRegisterError('');
    setRegisterSuccess('');

    // 表单验证
    if (!registerForm.username || !registerForm.password) {
      setRegisterError('请填写所有必填字段');
      setRegisterLoading(false);
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterError('密码和确认密码不匹配');
      setRegisterLoading(false);
      return;
    }

    if (registerForm.password.length < 6) {
      setRegisterError('密码长度至少为6位');
      setRegisterLoading(false);
      return;
    }

    try {
      // 调用注册API
      const response = await fetch('https://babadeke.com/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: registerForm.username,
          password: registerForm.password
        })
      });

      const result = await response.json();

      if (response.ok) {
        setRegisterSuccess('注册成功！请使用新账户登录。');
        
        // 清空表单
        setRegisterForm({
          username: '',
          password: '',
          confirmPassword: ''
        });
        
        // 3秒后关闭Modal
        setTimeout(() => {
          setShowRegisterModal(false);
        }, 3000);
      } else {
        // 确保错误信息是字符串
        const errorMessage = typeof result.detail === 'string' ? result.detail : 
                           result.message || '注册失败，请重试';
        setRegisterError(errorMessage);
      }
    } catch (error) {
      console.error('❌ 注册异常:', error);
      // 确保错误信息是字符串
      const errorMessage = error.message || '注册系统错误，请重试';
      setRegisterError(errorMessage);
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleRegisterInputChange = (field, value) => {
    setRegisterForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openRegisterModal = () => {
    setShowRegisterModal(true);
    setRegisterError('');
    setRegisterSuccess('');
    setRegisterForm({
      username: '',
      password: '',
      confirmPassword: ''
    });
  };

  const closeRegisterModal = () => {
    setShowRegisterModal(false);
    setRegisterError('');
    setRegisterSuccess('');
  };

  return (
    <Container
      header={
        <Header
          variant="h1"
          description="请输入您的用户名和密码进行登录"
        >
          家庭任务管理系统 - 登录
        </Header>
      }
    >
      <div style={{ 
        maxWidth: '400px', 
        margin: '0 auto', 
        padding: '2rem 0' 
      }}>
        {error && (
          <Alert
            type="error"
            header="登录失败"
            dismissible
            onDismiss={() => setError('')}
          >
            {error}
          </Alert>
        )}

        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                key="login"
                variant="primary" 
                loading={isLoading}
                onClick={handleSubmit}
              >
                登录
              </Button>
              <Button 
                key="reset"
                variant="normal"
                onClick={() => {
                  setUsername('');
                  setPassword('');
                  setError('');
                }}
              >
                重置
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            <FormField
              key="username"
              label="用户名"
              description="请输入您的用户名"
            >
              <Input
                value={username}
                onChange={({ detail }) => setUsername(detail.value)}
                placeholder="例如: admin"
                disabled={isLoading}
              />
            </FormField>

            <FormField
              key="password"
              label="密码"
              description="请输入您的密码"
            >
              <Input
                type="password"
                value={password}
                onChange={({ detail }) => setPassword(detail.value)}
                placeholder="请输入密码"
                disabled={isLoading}
              />
            </FormField>
          </SpaceBetween>
        </Form>

        {/* 注册提示 */}
        <div style={{ 
          marginTop: '2rem', 
          padding: '1rem',
          border: '1px solid var(--color-border-divider-default)',
          borderRadius: '8px',
          backgroundColor: 'var(--color-background-container-header)',
          textAlign: 'center'
        }}>
          <Header variant="h3">新用户注册</Header>
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-body-secondary)' }}>
            还没有账户？点击下方按钮进行注册。
          </p>
          <Button 
            variant="primary" 
            onClick={() => setShowRegisterModal(true)}
            fullWidth
          >
            注册新账户
          </Button>
        </div>

        {/* 提示信息 */}
        <div style={{ 
          marginTop: '1rem', 
          fontSize: '0.9rem',
          color: 'var(--color-text-body-secondary)',
          textAlign: 'center'
        }}>
          <p>如果遇到连接问题，请检查：</p>
          <ul style={{ textAlign: 'left', margin: '0.5rem 0' }}>
            <li>网络连接是否正常</li>
            <li>后端服务是否启动</li>
            <li>API地址配置是否正确</li>
          </ul>
        </div>
      </div>

      {/* 注册Modal */}
      <Modal
        visible={showRegisterModal}
        onDismiss={closeRegisterModal}
        header="注册新账户"
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button key="cancel" variant="link" onClick={closeRegisterModal}>
                取消
              </Button>
              <Button 
                key="submit"
                variant="primary" 
                loading={registerLoading}
                onClick={handleRegister}
              >
                注册
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {registerError && (
            <Alert
              key="error"
              type="error"
              header="注册失败"
              dismissible
              onDismiss={() => setRegisterError('')}
            >
              {registerError}
            </Alert>
          )}

          {registerSuccess && (
            <Alert
              key="success"
              type="success"
              header="注册成功"
              dismissible
              onDismiss={() => setRegisterSuccess('')}
            >
              {registerSuccess}
            </Alert>
          )}

          <FormField
            key="reg-username"
            label="用户名"
            description="设置您的登录用户名"
            constraintText="必填，用户名需唯一"
          >
            <Input
              value={registerForm.username}
              onChange={({ detail }) => handleRegisterInputChange('username', detail.value)}
              placeholder="例如: zhangsan"
              disabled={registerLoading}
            />
          </FormField>

          <FormField
            key="reg-password"
            label="密码"
            description="设置登录密码"
            constraintText="必填，至少6位字符"
          >
            <Input
              type="password"
              value={registerForm.password}
              onChange={({ detail }) => handleRegisterInputChange('password', detail.value)}
              placeholder="请输入密码"
              disabled={registerLoading}
            />
          </FormField>

          <FormField
            key="reg-confirm"
            label="确认密码"
            description="再次输入密码进行确认"
            constraintText="必填"
          >
            <Input
              type="password"
              value={registerForm.confirmPassword}
              onChange={({ detail }) => handleRegisterInputChange('confirmPassword', detail.value)}
              placeholder="请再次输入密码"
              disabled={registerLoading}
            />
          </FormField>

          <div key="notice" style={{ 
            fontSize: '0.9rem',
            color: 'var(--color-text-body-secondary)',
            padding: '1rem',
            backgroundColor: 'var(--color-background-container-header)',
            borderRadius: '8px'
          }}>
            <p><strong>注册说明：</strong></p>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              <li>注册后即可使用系统</li>
              <li>角色和成员信息可在登录后完善</li>
              <li>如有问题请联系系统管理员</li>
            </ul>
          </div>
        </SpaceBetween>
      </Modal>
    </Container>
  );
};

export default LoginForm;